import React from 'react';
import PropTypes from 'prop-types';

/*
The few inputs the Radio form repeats, styled like the Events form (white pill inputs
on the black modal). `error` takes a message or a list of them: the client-side check
gives one, the backend can give several for the same field.
*/

const toMessages = error => [].concat(error || []).filter(Boolean);

export const FieldErrors = ({ error }) => {
  const messages = toMessages(error);
  if (!messages.length) return null;
  return (
    <ul className="radio-field-errors">
      {messages.map(message => <li key={message}>{message}</li>)}
    </ul>
  );
};

FieldErrors.propTypes = {
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
};

FieldErrors.defaultProps = {
  error: null,
};

export const TextField = ({
  label, name, value, onChange, error, help, placeholder, type, disabled,
}) => (
  <div className="w-full">
    <label className="flex flex-col font-americaMonoRegular text-xs uppercase" htmlFor={name}>
      {label}
      <input
        id={name}
        name={name}
        type={type}
        value={value === null || value === undefined ? '' : value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={event => onChange(event.target.value)}
        className={`radio-input${toMessages(error).length ? ' has-error' : ''}`}
      />
    </label>
    {help ? <p className="radio-help">{help}</p> : null}
    <FieldErrors error={error} />
  </div>
);

TextField.propTypes = {
  label: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  help: PropTypes.node,
  placeholder: PropTypes.string,
  type: PropTypes.string,
  disabled: PropTypes.bool,
};

TextField.defaultProps = {
  value: '',
  error: null,
  help: null,
  placeholder: '',
  type: 'text',
  disabled: false,
};

// options: [{ value, label }]. A value that is not among them (a legacy row with no
// channel, say) is shown as its own option rather than silently as the first one.
export const SelectField = ({
  label, name, value, onChange, options: baseOptions, error, help,
}) => {
  const current = value === null || value === undefined ? '' : String(value);
  const options = baseOptions.some(option => String(option.value) === current)
    ? baseOptions
    : [{ value: current, label: current || '—' }, ...baseOptions];
  return (
    <div className="w-full">
      <label className="flex flex-col font-americaMonoRegular text-xs uppercase" htmlFor={name}>
        {label}
        <select
          id={name}
          name={name}
          value={current}
          onChange={event => onChange(event.target.value)}
          className={`radio-input${toMessages(error).length ? ' has-error' : ''}`}
        >
          {options.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </label>
      {help ? <p className="radio-help">{help}</p> : null}
      <FieldErrors error={error} />
    </div>
  );
};

SelectField.propTypes = {
  label: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.node,
  })).isRequired,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  help: PropTypes.node,
};

SelectField.defaultProps = {
  value: '',
  error: null,
  help: null,
};

export const CheckboxField = ({
  label, name, checked, onChange,
}) => (
  <label className="radio-checkbox font-americaMonoRegular" htmlFor={name}>
    <input
      id={name}
      name={name}
      type="checkbox"
      checked={Boolean(checked)}
      onChange={event => onChange(event.target.checked)}
    />
    {label}
  </label>
);

CheckboxField.propTypes = {
  label: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  checked: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

CheckboxField.defaultProps = {
  checked: false,
};

// react-select inside the scrolling modal: render the menu on <body> so it is not clipped.
export const portalSelectProps = () => (typeof document === 'undefined' ? {} : {
  menuPortalTarget: document.body,
  styles: { menuPortal: base => ({ ...base, zIndex: 100000 }) },
});

// The readable name of a radio tag or program title: Spanish first, then any language.
export const multilangLabel = (multilang, fallback = '') => {
  if (!multilang) return fallback;
  return multilang.es || multilang.en || multilang.ca || multilang.pt || fallback;
};
